<template>
    <div>
        <v-tabs v-model="productTab" color="primary">
            <v-tab value="information">{{ $t('information') }}</v-tab>
            <v-tab v-if="useIsShowSeatMap && useIsEditMode" value="seat-map">
                {{ $t('seat_map') }}
            </v-tab>
        </v-tabs>
        <v-tabs-window v-model="productTab">
            <v-tabs-window-item value="information">
                <v-row dense class="py-2">
                    <v-col cols="3">
                        <LocaleSwitcher @change="changeActiveLangCode" />
                    </v-col>
                    <v-col class="d-flex justify-end">
                        <v-switch
                            v-model="locationData.active"
                            :label="locationData.active ? $t('active') : $t('inactive')"
                            color="primary"
                            density="compact"
                            hide-details
                            data-cy="company-status"
                        />
                    </v-col>
                </v-row>
                <v-form ref="form" v-model="valid">
                    <v-row dense>
                        <v-col cols="6">
                            <v-row align="center">
                                <v-col cols="12">
                                    <TextFieldLocale
                                        v-model="locationData.name[activeLangCode]"
                                        :label="`${$t('name')}*`"
                                        :active-lang-code="activeLangCode"
                                        data-cy="name"
                                        :rules="[requiredRule('name')]"
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <city-autocomplete
                                        v-model="city"
                                        :label="`${$t('city')}*`"
                                        :show-icon="false"
                                        dense
                                        :rules="[requiredRule('city')]"
                                        @change="country = {name: city.countryName, id: city.countryId}"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <TextFieldLocale
                                        v-model="locationData.street[activeLangCode]"
                                        :label="$t('address')"
                                        :active-lang-code="activeLangCode"
                                        data-cy="street"
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field
                                        v-model="locationData.postalCode"
                                        :label="$t('postal_code')"
                                        density="compact"
                                        variant="outlined"
                                        color="primary"
                                        hide-details
                                        data-cy="postal-code"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field
                                        v-model="locationData.latitude"
                                        :label="$t('latitude')"
                                        density="compact"
                                        variant="outlined"
                                        color="primary"
                                        type="text"
                                        hide-details
                                        class="input--no-arrows"
                                        data-cy="latitude"
                                        @update:model-value="getFormattedCoordinates($event, 'latitude')"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field
                                        v-model="locationData.longitude"
                                        :label="$t('longitude')"
                                        density="compact"
                                        variant="outlined"
                                        color="primary"
                                        type="text"
                                        hide-details
                                        class="input--no-arrows"
                                        data-cy="longitude"
                                        @update:model-value="getFormattedCoordinates($event, 'longitude')"
                                    />
                                </v-col>
                                <!--waiting google key-->
                                <!--<v-col cols="2">-->
                                <!--    <v-btn-->
                                <!--        icon="mdi-map-search"-->
                                <!--        color="primary"-->
                                <!--        variant="text"-->
                                <!--        size="small"-->
                                <!--        @click="getCoordinates"-->
                                <!--    />-->
                                <!--</v-col>-->
                                <v-col cols="12">
                                    <v-container fluid class="pa-0">
                                        <div>{{ `${t('available_for')}:` }}</div>
                                        <v-checkbox
                                            v-model="eventTypes"
                                            :label="t('sport')"
                                            value="SPORT"
                                            density="compact"
                                            hide-details
                                        />
                                        <v-checkbox
                                            v-model="eventTypes"
                                            :label="t('concert')"
                                            value="CONCERT"
                                            density="compact"
                                            hide-details
                                        />
                                        <v-checkbox
                                            v-model="eventTypes"
                                            :label="t('other')"
                                            value="OTHER"
                                            density="compact"
                                            hide-details
                                        />
                                    </v-container>
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col cols="6">
                            <MapLocation
                                :city="city"
                                :latitude="locationData.latitude ? Number(locationData.latitude) : null"
                                :longitude="locationData.longitude ? Number(locationData.longitude) : null"
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </v-tabs-window-item>
            <v-tabs-window-item value="seat-map">
                <v-row dense class="mb-5">
                    <v-col cols="12" md="5">
                        <SeatMapForm :linked-object-id="venue.id" seat-type="VENUE" @cancel="emit('go-to-table')" />
                    </v-col>
                </v-row>
            </v-tabs-window-item>
        </v-tabs-window>
        <template v-if="productTab === 'information'">
            <div class="d-flex align-center mt-4">
                <v-btn color="primary" :loading="isLoading" :disabled="isLoading" data-cy="submit" @click="save">
                    {{ $t('save') }}
                </v-btn>
                <v-btn variant="text" @click="emit('go-to-table')">
                    {{ $t('cancel') }}
                </v-btn>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import type {VenueType} from '~/utils/types/account/locations/types'
    import CityAutocomplete from '~/components/search/forms/CityAutocomplete.vue'
    import MapLocation from '~src/components/info/mapLocation.src'
    import {clean, clone} from '~/utils/helpers'
    import SeatMapForm from '~/components/account/seatMap/SeatMapForm.vue'
    import {requiredRule} from '~/composables/rules'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'

    const props = defineProps<{
        venue: VenueType
    }>()

    const emit = defineEmits(['load-locations', 'go-to-table'])

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (props.venue) {
            locationData.value = clone(props.venue)
            city.value = locationData.value.cityId
                ? {
                      name: locationData.value.cityName,
                      id: locationData.value.cityId,
                      countryName: locationData.value.countryName,
                      parentName: locationData.value.countryName,
                  }
                : null
            eventTypes.value = locationData.value.types
        }
    })

    const productTab = ref<string>('information')
    const isLoading = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const locationData = ref<VenueType>(props.venue)
    const city = ref<{
        name?: string
        id?: number
        countryName?: string
        parentName?: string
        latitude?: number
        longitude?: number
    }>(null)
    const country = ref<{name?: string; id?: number}>(null)
    const eventTypes = ref<string[]>([])
    const form = ref(null)
    const valid = ref(false)

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
    })

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    const useIsShowSeatMap = computed(() => {
        if (!authStore.isTO1 && ['to1l.supervisor', 'to1l.director', 'to1l.support'].includes(authStore.person.role)) {
            return false
        }
        return runtimeStore.getCRMOption('asp.option.ownEvents.show.seats') || false
    })

    const useIsEditMode = computed(() => {
        return props.venue?.id
    })

    async function save(): Promise<void> {
        await form.value.validate()
        if (!valid.value) return
        isLoading.value = true
        const {id, street, name, longitude, latitude, active, postalCode} = locationData.value
        const rq = {
            ...(id && {id}),
            ...(street && {street}),
            name,
            ...(longitude && {longitude}),
            ...(latitude && {latitude}),
            ...(postalCode && {postalCode}),
            active,
            types: eventTypes.value,
            cityId: city.value.id,
            supplierId: 1,
            supplierSpecificCode: 1,
        }
        clean(rq)
        try {
            if (locationData.value?.id) await app.$api.venues.put(locationData.value.id, rq)
            else await app.$api.venues.post(rq)
            emit('load-locations')
            emit('go-to-table')
            app.$toast.success(t('success_message.changed_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    //TODO [P4] duplicate from LocationPointDetailsModal
    function getFormattedCoordinates(v: number | string, key: string): void {
        v = String(v).replace(',', '.')
        if (isNaN(parseFloat(v))) {
            locationData.value[key] = null
            return
        }
        const count = 100000
        locationData.value[key] = Math.floor(parseFloat(v) * count) / count
    }

    //TODO [P4] duplicate from LocationPointDetailsModal
    async function getCoordinates(): Promise<void> {
        try {
            isLoading.value = true
            const address = locationData.value.street || ''
            const cityName = city.value.name || ''
            const countryName = country.value.name || ''
            const postalCode = locationData.value.postalCode || ''
            const rq = {
                street_address: address,
                locality: cityName,
                country: countryName,
                postal_code: postalCode,
            }
            const {results} = await app.$api.geocode.get(rq)

            // TODO [P4]: make possibility to choose result
            if (results?.length) {
                locationData.value.latitude = results[0].geometry.location.lat
                locationData.value.longitude = results[0].geometry.location.lng
            }
        } catch (e) {
            console.error(e)
        } finally {
            isLoading.value = false
        }
    }
</script>
